import { inject, Injectable } from '@angular/core'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { catchError, Observable, of, tap, throwError } from 'rxjs'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionsOrderRequestInterface } from '@modules/options-module/orders/interfaces/options-order-request.interface'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { ChangedSimpleOrderInterface } from '@modules/orders-module/interfaces/changed-simple-order.interface'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { AlertService } from '@shared/services/alert.service'
import { UtilsService } from '@shared/services/utils.service'

@Injectable({
	providedIn: 'root',
})
export class OptionsOrdersService {
	protected readonly object: ObjectConstructor = Object
	protected readonly string: StringConstructor = String

	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _websocketService: WebsocketService<OptionsOrderRequestInterface, OptionOrderInterface[]> =
		inject<WebsocketService<OptionsOrderRequestInterface, OptionOrderInterface[]>>(WebsocketService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	public getOptionsOrders(state: OptionOrderStateEnum): Observable<OrdersResponseInterface<OptionOrderInterface>> {
		const ordersParams: OrderParamsInterface<OptionOrderStateEnum> | undefined =
			this._optionsOrderHelperService.optionsOrdersParamsMap.get(state)?.getValue()
		if (!ordersParams) return of()

		const params: HttpParams = new HttpParams()
			.set('status', ordersParams.status)
			.set('page', ordersParams.page)
			.set('page_size', ordersParams.pageSize)

		this._optionsOrderHelperService.setLoadingByState(state, true)

		return this._http
			.get<
				OrdersResponseInterface<OptionOrderInterface>
			>(`${this._environmentsHelperService.apiUrl}/options/v1/options_orders_list/`, { params })
			.pipe(
				catchError((error: HttpErrorResponse): Observable<OrdersResponseInterface<OptionOrderInterface>> => {
					this._alertService.error(error.error.error.message)
					this._optionsOrderHelperService.setLoadingByState(state, false)
					return of()
				}),
				tap((response: OrdersResponseInterface<OptionOrderInterface>): void => {
					this._optionsOrderHelperService.setOrderResponseByState(response, state)
					this._optionsOrderHelperService.setLoadingByState(state, false)
				}),
			)
	}

	public launchSocketOpenedOrdersOptions(status: OptionOrderStateEnum): Observable<OptionOrderInterface[]> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of([])

		const url: string = `${this._environmentsHelperService.socketUrl}/ws/user_options_orders/`

		this._websocketService.connect(url)

		if (!this._websocketService.hasMessageInSocket(url)) {
			this._websocketService.sendMessage(url, {
				action: 'get_orders',
				request_id: this._utilsService.generateUniqId(),
				user_id: userDetail.id,
				status,
			})
		}

		return this._websocketService.getResponses(url).pipe(
			tap((orders: OptionOrderInterface[]): void => {
				this._optionsOrderHelperService.putOrdersFromSocket(orders, status)
			}),
		)
	}

	public createOptionOrder(order: CreateOrderInterface): Observable<OptionOrderInterface> {
		if (this._userHelperService.checkIsLimited()) return throwError((): null => null)

		return this._http.post<OptionOrderInterface>(
			`${this._environmentsHelperService.apiUrl}/orders/create_order/`,
			order,
		)
	}

	public cancelOptionOrder(orderId: string): Observable<OptionOrderInterface> {
		return this._http.post<OptionOrderInterface>(`${this._environmentsHelperService.apiUrl}/orders/cancel_order/`, {
			order_id: orderId,
		})
	}

	public changeOptionOrder(order: ChangedSimpleOrderInterface): Observable<OptionOrderInterface> {
		return this._http.patch<OptionOrderInterface>(
			`${this._environmentsHelperService.apiUrl}/orders/replace_order/`,
			order,
		)
	}
}
